// frontend/src/pages/client/SettingsClient.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cog6ToothIcon,
  UserIcon,
  LockClosedIcon,
  IdentificationIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ShieldCheckIcon,
  CheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/solid';
import API from '../../services/api';
import './SettingsClient.css';

const SettingsClient = () => {
  const timerRef = useRef(null);
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab]         = useState('profile');
  const [msg, setMsg]         = useState(null);

  const [profile, setProfile] = useState({
    first_name: '',
    last_name:  '',
    email:      '',
    phone:      '',
  });

  const [passwords, setPasswords] = useState({
    current_password:  '',
    new_password:      '',
    confirm_password:  '',
  });

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(u);
    setProfile({
      first_name: u.first_name || '',
      last_name:  u.last_name  || '',
      email:      u.email      || '',
      phone:      u.phone      || '',
    });
    return () => clearTimeout(timerRef.current);
  }, []);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setMsg(null), 4000);
  };

  const handleSaveProfile = async () => {
    if (!profile.first_name || !profile.last_name) {
      showMsg('error', 'Prénom et nom sont requis');
      return;
    }
    setLoading(true);
    try {
      await API.put('/client/profile', profile);
      const u = { ...user, ...profile };
      localStorage.setItem('user', JSON.stringify(u));
      setUser(u);
      showMsg('success', 'Profil mis à jour avec succès');
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwords.current_password || !passwords.new_password) {
      showMsg('error', 'Tous les champs sont requis');
      return;
    }
    if (passwords.new_password.length < 8) {
      showMsg('error', 'Minimum 8 caractères');
      return;
    }
    if (passwords.new_password !== passwords.confirm_password) {
      showMsg('error', 'Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      await API.put('/auth/client/password', passwords);
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
      showMsg('success', 'Mot de passe modifié avec succès');
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Mot de passe actuel incorrect');
    } finally {
      setLoading(false);
    }
  };

  const TABS = [
    { id: 'profile',  label: 'Profil',   Icon: UserIcon },
    { id: 'security', label: 'Sécurité', Icon: LockClosedIcon },
    { id: 'account',  label: 'Compte',   Icon: IdentificationIcon },
  ];

  return (
    <div className="settings-partner-pro">

      {/* ── En-tête ── */}
      <motion.div
        className="settings-header-pro"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="settings-header-icon">
          <Cog6ToothIcon style={{ width: 28, height: 28 }} />
        </div>
        <div>
          <h1>Paramètres</h1>
          <p>Gérez votre profil et la sécurité de votre compte</p>
        </div>
      </motion.div>

      {/* ── Alert ── */}
      <AnimatePresence>
        {msg && (
          <motion.div
            className={`alert-pro ${msg.type}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {msg.type === 'success'
              ? <CheckCircleIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
              : <ExclamationCircleIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
            }
            {msg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Tabs ── */}
      <div className="settings-tabs-pro">
        {TABS.map(({ id, label, Icon }) => (
          <motion.button
            key={id}
            className={`settings-tab-btn ${tab === id ? 'active' : ''}`}
            onClick={() => setTab(id)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Icon style={{ width: 16, height: 16 }} />
            {label}
          </motion.button>
        ))}
      </div>

      {/* ── Contenu ── */}
      <AnimatePresence mode="wait">

        {/* ── Profil ── */}
        {tab === 'profile' && (
          <motion.div
            key="profile"
            className="settings-card-pro"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <h3>
              <UserIcon style={{ width: 18, height: 18, color: '#06b6d4' }} />
              Informations personnelles
            </h3>

            <div className="settings-form-grid">
              <div className="form-group-pro">
                <label>Prénom</label>
                <input
                  type="text"
                  value={profile.first_name}
                  onChange={e => setProfile({ ...profile, first_name: e.target.value })}
                  placeholder="Votre prénom"
                />
              </div>
              <div className="form-group-pro">
                <label>Nom</label>
                <input
                  type="text"
                  value={profile.last_name}
                  onChange={e => setProfile({ ...profile, last_name: e.target.value })}
                  placeholder="Votre nom"
                />
              </div>
              <div className="form-group-pro">
                <label>Email</label>
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  placeholder="votre@email.com"
                />
              </div>
              <div className="form-group-pro">
                <label>Téléphone</label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={e => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="+33 6 00 00 00 00"
                />
              </div>
            </div>

            <div className="settings-footer-pro">
              <motion.button
                className="btn-save-pro"
                onClick={handleSaveProfile}
                disabled={loading}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {loading
                  ? <><ArrowPathIcon style={{ width: 16, height: 16 }} className="icon-spin" /> Enregistrement…</>
                  : <><CheckIcon style={{ width: 16, height: 16 }} /> Enregistrer</>
                }
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* ── Sécurité ── */}
        {tab === 'security' && (
          <motion.div
            key="security"
            className="settings-card-pro"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <h3>
              <LockClosedIcon style={{ width: 18, height: 18, color: '#06b6d4' }} />
              Changer le mot de passe
            </h3>

            <div className="settings-form-grid single">
              {[
                { key: 'current_password', label: 'Mot de passe actuel',         placeholder: '••••••••' },
                { key: 'new_password',     label: 'Nouveau mot de passe',         placeholder: 'Min. 8 caractères' },
                { key: 'confirm_password', label: 'Confirmer le nouveau mot de passe', placeholder: '••••••••' },
              ].map(f => (
                <div key={f.key} className="form-group-pro">
                  <label>{f.label}</label>
                  <input
                    type="password"
                    value={passwords[f.key]}
                    onChange={e => setPasswords({ ...passwords, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                  />
                </div>
              ))}
            </div>

            <div className="info-banner">
              <ShieldCheckIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
              <span>Utilisez au moins 8 caractères avec des lettres et des chiffres</span>
            </div>

            <div className="settings-footer-pro">
              <motion.button
                className="btn-save-pro"
                onClick={handleChangePassword}
                disabled={loading}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {loading
                  ? <><ArrowPathIcon style={{ width: 16, height: 16 }} className="icon-spin" /> Modification…</>
                  : <><LockClosedIcon style={{ width: 16, height: 16 }} /> Changer le mot de passe</>
                }
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* ── Compte ── */}
        {tab === 'account' && (
          <motion.div
            key="account"
            className="settings-card-pro"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <h3>
              <IdentificationIcon style={{ width: 18, height: 18, color: '#06b6d4' }} />
              Informations du compte
            </h3>

            <div className="account-info-grid">
              <div className="account-info-item">
                <span className="info-label">Email</span>
                <span className="info-value">{user?.email || '—'}</span>
              </div>
              <div className="account-info-item">
                <span className="info-label">Statut du compte</span>
                <span className="badge-pro badge-success">
                  <CheckCircleIcon style={{ width: 14, height: 14 }} /> Actif
                </span>
              </div>
              <div className="account-info-item">
                <span className="info-label">Type de compte</span>
                <span className="info-value">Client</span>
              </div>
              <div className="account-info-item">
                <span className="info-label">Membre depuis</span>
                <span className="info-value">
                  {user?.created_at
                    ? new Date(user.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric'
                      })
                    : '—'}
                </span>
              </div>
            </div>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
};

export default SettingsClient;
